import { recordMatch, fetchGameHistory, getStats } from '@bn-games/game-history-client';
import type { GameStats, MatchOutcome, MatchRecord } from '@bn-games/game-history-client';
import { normalizeVariantId } from '../game/variants/index.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';
import type { GameRecord, RecordedSeed } from './types.js';

/**
 * Write-side mapping: a finished local `GameRecord` -> the generic outcome
 * enum the server understands, from the recording player's own perspective.
 * `null` means "can't be determined from this record" — the caller skips the
 * sync entirely rather than guess. `result` is checked before `winnerSeats`
 * — the abandon call site always records an empty `winnerSeats`, so checking
 * `winnerSeats.includes(localSeat)` first would misreport an abandoned match
 * as a loss.
 */
function computeOutcome(record: GameRecord): MatchOutcome | null {
  const localSeat = record.seed.localSeat;
  if (localSeat === null) return null;
  if (record.result === 'draw') return 'draw';
  if (record.result === 'abandoned') return 'abandoned';
  // result === 'win'
  return record.winnerSeats.includes(localSeat) ? 'win' : 'loss';
}

/**
 * Awaitable sync of one finished ONLINE record to the server, over
 * `recordMatch`'s own success/failure result — used both as the immediate
 * sync attempt and as the outbox's retry `send` callback (M7c). Routing
 * (whether this is even called, and whether the caller also writes
 * `localStorage`) is `commitFinishedRecord`'s job, not this function's — it
 * no longer checks `isLoggedIn()` or `record.seed.mode` itself.
 *
 * Returns `true` when the record should be considered delivered — including
 * the (should-be-unreachable for a real online match) case where no outcome
 * can be computed, since retrying that record would never succeed either.
 */
export async function syncOnlineRecord(record: GameRecord): Promise<boolean> {
  const outcome = computeOutcome(record);
  if (outcome === null) return true;
  // Domino has no `{white, black}`-shaped roster like checkers/backgammon, so
  // the wire's generically-named `players` field (required, opaque to the
  // Worker) is repurposed here to carry the per-seat match summary instead.
  const result = await recordMatch('domino', {
    id: record.id,
    variant: record.variant,
    outcome,
    players: {
      playerCount: record.playerCount,
      finalScores: record.finalScores,
      winnerSeats: record.winnerSeats,
    },
    seed: record.seed,
    moves: record.moves,
    createdAt: record.createdAt,
    finishedAt: record.finishedAt,
  });
  return result.ok;
}

/**
 * Read-side inverse of `computeOutcome`. A `'loss'` is still a completed
 * `result: 'win'` record from the other seats' perspective — `GameRecord`'s
 * `result` field only distinguishes win/draw/abandoned, never who won beyond
 * `winnerSeats`.
 */
function outcomeToResult(outcome: MatchOutcome): 'win' | 'draw' | 'abandoned' {
  if (outcome === 'draw') return 'draw';
  if (outcome === 'abandoned') return 'abandoned';
  return 'win';
}

interface RemotePlayersSummary {
  readonly playerCount: 2 | 3 | 4;
  readonly finalScores: readonly number[];
  readonly winnerSeats: readonly number[];
}

function isRemotePlayersSummary(value: unknown): value is RemotePlayersSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const { playerCount, finalScores, winnerSeats } = candidate;
  if (playerCount !== 2 && playerCount !== 3 && playerCount !== 4) return false;
  if (!Array.isArray(finalScores)) return false;
  if (!Array.isArray(winnerSeats)) return false;
  return true;
}

/**
 * Light-touch structural validation, matching the local `HistoryStore`'s own
 * `isValid` philosophy (structural sanity, not exhaustive re-validation of a
 * payload this same client wrote in the first place).
 */
export function toRemoteGameRecord(remote: MatchRecord): GameRecord | null {
  // Server rows keep the id they were recorded with (`kozyol` = Телефон).
  const variant = normalizeVariantId(remote.variant);
  if (variant === null) return null;
  if (typeof remote.seed !== 'object' || remote.seed === null) return null;
  if (!Array.isArray(remote.moves)) return null;
  if (!isRemotePlayersSummary(remote.players)) return null;

  const rawSeed = remote.seed as RecordedSeed;
  if (typeof rawSeed.localSeat !== 'number') return null;
  const seed: RecordedSeed = { ...rawSeed, variant: normalizeVariantId(rawSeed.variant) ?? variant };

  const { playerCount, finalScores, winnerSeats } = remote.players;
  const result = outcomeToResult(remote.outcome);

  return {
    id: remote.id,
    version: HISTORY_SCHEMA_VERSION,
    createdAt: remote.createdAt,
    finishedAt: remote.finishedAt,
    mode: 'online',
    variant,
    playerCount,
    finalScores,
    winnerSeats,
    result,
    seed,
    moves: remote.moves as GameRecord['moves'],
  };
}

/** Best-effort fetch of the signed-in player's server-synced online history. */
export function fetchOnlineHistory(): Promise<GameRecord[]> {
  return fetchGameHistory('domino', toRemoteGameRecord);
}

/**
 * This game's slice of `GET /history/stats`, for the account section's
 * one-line summary. `null` on any failure (network, signed-out, decode) —
 * the account section already only calls this while signed in, and treats a
 * missing stats line as "nothing to show" rather than an error.
 */
export async function fetchDominoStats(): Promise<GameStats | null> {
  const result = await getStats();
  return result.ok ? result.value.byGame.domino : null;
}
